/**
 * BonfireSkillEffect — Fire tree Ember Fork 2-B: stationary fire anchor.
 *
 * Place a stationary 1-tile fire anchor at the player's current position with a
 * 3-tile aura radius. The zone does NOT follow the player — it stays where it
 * was placed. Same HP regen and DoT behavior as Ember:
 *
 *   - Every 1000ms: each enemy inside takes 2 direct fire damage (not burning DoT).
 *   - Every 3000ms: the player heals 1% max HP per enemy currently inside the zone.
 *
 * Lasts 12 seconds base. Max 1 active — placing a second removes the first.
 * Long-tier cooldown. Fizzle chance at unlock (cooldown consumed on fizzle).
 *
 * Wired for upgrades:
 *   - Hearthstone (`bonfire_radius`): +1.5 radiusTiles, +6000ms duration.
 *   - Deep Hearth (`bonfire_regen`): ×2 regen multiplier via bonfireRegenMultiplier.
 *   - Hearth passive (`ember_path_passive`): stationary regen multiplier (tree-wide).
 *
 * Breaks Hide on activation (the fire is visible).
 *
 * @see fire-spec.md § Bonfire (`bonfire`)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone config
// ---------------------------------------------------------------------------

export interface BonfireZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly sourceSkillId: string;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BonfireDeps {
  /** Returns the player's current world position in pixels. */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Returns the player's entity ID. */
  readonly getPlayerEntityId: () => string;
  /** Returns entity IDs of enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];
  /** Deal direct damage to a target entity. */
  readonly dealDamage: (entityId: string, damage: number) => void;
  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };
  /** Break stealth / hide state. */
  readonly breakHide: () => void;
  readonly zones: {
    create(config: BonfireZoneConfig): string;
    remove(zoneId: string): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  /** Returns true if activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Returns how long the player has stood still (ms). Used by Hearth passive. */
  readonly getStationaryDurationMs?: () => number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BonfireConfig {
  /** Cooldown after placement in ms. Long tier. */
  readonly cooldownMs: number;
  /** Base aura radius in tiles. */
  readonly radiusTiles: number;
  /** Base duration in ms. */
  readonly durationMs: number;
  /** Direct fire damage per enemy per damage tick. */
  readonly damagePerTick: number;
  /** Interval between damage ticks in ms. */
  readonly damageTickMs: number;
  /** Fraction of max HP healed per enemy per heal tick. */
  readonly healPercent: number;
  /** Interval between heal ticks in ms. */
  readonly healTickMs: number;
}

const DEFAULT_CONFIG: BonfireConfig = {
  cooldownMs: 2000,         // long cooldown tier
  radiusTiles: 3,           // spec: 3-tile radius
  durationMs: 12000,        // spec: 12 seconds
  damagePerTick: 2,         // spec: 2 damage per tick (same as Ember)
  damageTickMs: 1000,       // spec: every 1000ms
  healPercent: 0.01,        // spec: 1% max HP per enemy
  healTickMs: 3000,         // spec: every 3 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BonfireSkillEffect implements SkillEffect {
  readonly skillId = 'bonfire';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly _deps: BonfireDeps;
  private readonly _config: BonfireConfig;

  // State
  private _zoneId: string | null = null;
  private _zonePosition: { x: number; y: number } | null = null;
  private _durationRemaining = 0;
  private _cooldownRemaining = 0;

  // Tick accumulators
  private _damageAccumulator = 0;
  private _healAccumulator = 0;

  // Stats (for scenario assertions)
  private _activations = 0;
  private _fizzles = 0;
  private _totalDamageDealt = 0;
  private _totalHealAmount = 0;

  constructor(deps: BonfireDeps, config: Partial<BonfireConfig> = {}) {
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get totalHealAmount(): number { return this._totalHealAmount; }
  get zoneId(): string | null { return this._zoneId; }
  get zonePosition(): { x: number; y: number } | null { return this._zonePosition; }
  get durationRemaining(): number { return this._durationRemaining; }

  // --- SkillEffect interface ---

  isActive(): boolean { return this._zoneId !== null; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle.
    if (!this._deps.rollSkillSuccess('bonfire')) {
      this._fizzles++;
      this._cooldownRemaining = this._config.cooldownMs;
      return false;
    }

    this._deps.breakHide();

    // Max 1 active: remove existing zone before placing a new one.
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
      this._zonePosition = null;
    }

    const pos = this._deps.getPlayerPosition();

    // Resolve additive bonuses from Hearthstone upgrade.
    const bonusRadius = this._deps.resolver.getAdditiveEffect('bonfire', 'radiusTiles');
    const resolvedRadius = this._config.radiusTiles + bonusRadius;

    const bonusDuration = this._deps.resolver.getAdditiveEffect('bonfire', 'durationMs');
    const resolvedDuration = this._config.durationMs + bonusDuration;

    // Create stationary zone — no followEntity.
    this._zoneId = this._deps.zones.create({
      id: `bonfire-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'bonfire_zone',
      position: { x: pos.x, y: pos.y },
      shape: 'circle',
      radiusTiles: resolvedRadius,
      sourceSkillId: 'bonfire',
    });

    this._zonePosition = { x: pos.x, y: pos.y };
    this._durationRemaining = resolvedDuration;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
    this._activations++;
    this._cooldownRemaining = this._config.cooldownMs;
    return true;
  }

  deactivate(): void {
    if (!this._zoneId) return;
    this._deps.zones.remove(this._zoneId);
    this._zoneId = null;
    this._zonePosition = null;
    this._durationRemaining = 0;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._zoneId || !this._zonePosition) return;

    // Duration countdown.
    this._durationRemaining -= delta;
    if (this._durationRemaining <= 0) {
      this.deactivate();
      return;
    }

    this._damageAccumulator += delta;
    this._healAccumulator += delta;

    // Resolve current aura radius (base + Hearthstone bonus).
    const bonusRadius = this._deps.resolver.getAdditiveEffect('bonfire', 'radiusTiles');
    const resolvedRadius = this._config.radiusTiles + bonusRadius;

    // Damage ticks — direct fire damage to enemies inside the zone.
    while (this._damageAccumulator >= this._config.damageTickMs) {
      this._damageAccumulator -= this._config.damageTickMs;
      this._applyDamageTick(resolvedRadius);
    }

    // Heal ticks — player heals based on enemy count inside the zone.
    while (this._healAccumulator >= this._config.healTickMs) {
      this._healAccumulator -= this._config.healTickMs;
      this._applyHealTick(resolvedRadius);
    }
  }

  destroy(): void {
    if (this._zoneId) {
      this._deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this._zonePosition = null;
    this._durationRemaining = 0;
    this._damageAccumulator = 0;
    this._healAccumulator = 0;
  }

  // ---------------------------------------------------------------------------
  // Internal — tick logic
  // ---------------------------------------------------------------------------

  private _applyDamageTick(radiusTiles: number): void {
    const pos = this._zonePosition!;
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radiusTiles);

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, this._config.damagePerTick);
      this._totalDamageDealt += this._config.damagePerTick;
    }
  }

  private _applyHealTick(radiusTiles: number): void {
    const pos = this._zonePosition!;
    const enemies = this._deps.getEnemiesInRadius(pos.x, pos.y, radiusTiles);
    const enemyCount = enemies.length;

    if (enemyCount === 0) return;

    // Base heal percent.
    let resolvedHealPercent = this._config.healPercent;

    // Deep Hearth: bonfireRegenMultiplier (×2 when unlocked).
    const regenMultiplier = this._deps.resolver.getMultiplicativeEffect('bonfire', 'bonfireRegenMultiplier');
    if (regenMultiplier > 0) {
      resolvedHealPercent *= regenMultiplier;
    }

    // Hearth passive: stationary regen multiplier (tree-wide).
    const triggerMs = this._deps.resolver.getTreePassiveEffect('fire', 'stationaryTriggerMs');
    if (triggerMs > 0) {
      const stationaryMs = this._deps.getStationaryDurationMs?.() ?? 0;
      if (stationaryMs >= triggerMs) {
        const stationaryMultiplier = this._deps.resolver.getTreePassiveEffect(
          'fire',
          'regenMultiplier',
        );
        if (stationaryMultiplier > 0) {
          resolvedHealPercent *= stationaryMultiplier;
        }
      }
    }

    const healAmount = Math.round(
      this._deps.player.maxHp * resolvedHealPercent * enemyCount,
    );
    if (healAmount > 0) {
      this._deps.player.heal(healAmount);
      this._totalHealAmount += healAmount;
    }
  }
}
